/* eslint-disable react/prop-types */
import type { ReactElement } from 'react';
import React from 'react';
import clsx from 'clsx';
import { endOfDay } from 'date-fns';
import DatePicker, { type DatePickerProps } from 'react-datepicker';
import { FormGroup, FormText, Input, InputGroup, Label } from 'reactstrap';
import ErrorMessage from 'atom-components/form-fields-for-formik/error-message/error-message';
import { useFastField } from 'atom-components/form-fields-for-formik/field/hooks';
import type { FieldProps } from 'atom-components/form-fields-for-formik/types';
import { formatDateForAPI } from 'helpers/date.helpers';
import './date-field.scss';

export type DateFieldProps = FieldProps &
  Omit<
    DatePickerProps,
    'selectsRange' | 'selectsMultiple' | 'showMonthYearDropdown' | 'onChange'
  > & { onChange?: (dateString: string) => void };

export default function DateField({
  label,
  name,
  formGroupClassName,
  className,
  disabled,
  helperMessage,
  onBlur,
  onChange: onChangeCallback,
  hideSuccessValidation,
  hideErrorValidation,
  containerZIndex,
  ...props
}: DateFieldProps): ReactElement {
  const [, { value, error, touched }, { setValue, setTouched }] = useFastField<string>(name);
  const isValid = touched && !error && !hideSuccessValidation;
  const isError = touched && !!error && !hideErrorValidation;

  return (
    <FormGroup
      className={clsx(
        hideErrorValidation && hideSuccessValidation ? 'pb-1' : 'pb-3',
        formGroupClassName
      )}
    >
      {!!label && <Label>{label}</Label>}
      <InputGroup>
        <div
          style={{ zIndex: containerZIndex || 99 }} // Ensure that Date Picker always appears overtop of other elements
          className={clsx('react-datepicker-wrapper', {
            'is-invalid': isError,
            'is-valid': isValid
          })}
        >
          <DatePicker
            {...props}
            className={clsx(className, { 'is-invalid': isError, 'is-valid': isValid })}
            customInput={<Input />}
            disabled={disabled}
            dateFormat="dd/MM/yyyy"
            selected={value ? new Date(value) : null}
            onChange={(newValue): void => {
              const coercedDate = props.selectsEnd && newValue ? endOfDay(newValue) : newValue; // if end date set time to end of day
              const processedValue = coercedDate ? formatDateForAPI(coercedDate) : '';
              setValue(processedValue);
              setTimeout(() => setTouched(true, true), 1);

              if (onChangeCallback) onChangeCallback(processedValue);
            }}
            onBlur={(event): void => {
              setTouched(true, true);
              if (onBlur) {
                onBlur(event);
              }
            }}
          />
        </div>
        <ErrorMessage isError={isError} error={error} />
      </InputGroup>
      {helperMessage && <FormText>{helperMessage}</FormText>}
    </FormGroup>
  );
}
